import { groups } from 'd3';
import { LocationDetailLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { computeTotalSumByTradeFlow, getLocationLevelFromStringLocationId, fillMissingYearObservations } from "@atlas-int/data";
import { groupNames } from "@atlas-int/tokens/classifications";
import { LocationLevel } from "@atlas-int/data/graphql";
import type { YearRangeForProductClass } from './types';
import { TradeValueScaledByYAxisOption } from './constants';
import {
  createLocationHierarchyIndex,
  enrichLocationWithHierarchyFast,
  createYearLookupIndex,
} from "@atlas-int/data";
import type { TradeDataItem } from "@atlas-int/data/types";
import type { DeflatorDatum, RegionMetadata, CountryMetadata } from "@atlas-int/data/types";

/** Trade value pair for a specific scaling option */
interface TradeValuePair {
  exportValue: number;
  importValue: number;
}

/** Trade values across scaling options */
interface ScaledTradeValues {
  [TradeValueScaledByYAxisOption.Current]: TradeValuePair;
  [TradeValueScaledByYAxisOption.Constant]: TradeValuePair;
}

/** Enriched data item after adding hierarchy + trade values */
interface EnrichedLocationItem extends ScaledTradeValues {
  countryId?: string;
  groupId?: string;
  locationLevel: string;
  partnerId: number;
  partnerLevel: string;
  year: number;
  partnersData: TradeDataItem[];
  topLevelParent?: string;
  hybrid_level_1: { id: string; nameEn: string };
  hybrid_level_2: { id: string; nameEn: string };
  nameEn: string;
  countryIso3Code?: string;
}

/** Grouped location item (region/subregion level) */
interface GroupedLocationItem extends Partial<HierarchicalTradeValues> {
  countryId: string;
  partnerId: string | undefined;
  year: number;
  nameEn: string;
  topLevelParent: string;
}

/** Trade values for hierarchical grouping (may have undefined values) */
interface HierarchicalTradeValues {
  [TradeValueScaledByYAxisOption.Current]: { exportValue: number | undefined; importValue: number | undefined };
  [TradeValueScaledByYAxisOption.Constant]: { exportValue: number | undefined; importValue: number | undefined };
}

/**
 * Computes scaled trade values for locations
 */
const computeScaledTradeValues = ({
  partnersData,
  deflatorForYearMatchingMetadata,
  tradeFlow,
}: {
  partnersData: TradeDataItem[];
  deflatorForYearMatchingMetadata: DeflatorDatum | undefined;
  tradeFlow: TradeFlow;
}): ScaledTradeValues => {
  const tradeValues: ScaledTradeValues = {
    [TradeValueScaledByYAxisOption.Current]: { exportValue: 0, importValue: 0 },
    [TradeValueScaledByYAxisOption.Constant]: { exportValue: 0, importValue: 0 },
  };

  if (!partnersData || partnersData.length === 0) {
    return tradeValues;
  }

  const sumExportValue =
    computeTotalSumByTradeFlow({
      data: partnersData,
      tradeFlow,
      tradeDirection: TradeDirection.Exports,
    }) ?? 0;

  const sumImportValue =
    computeTotalSumByTradeFlow({
      data: partnersData,
      tradeFlow,
      tradeDirection: TradeDirection.Imports,
    }) ?? 0;

  tradeValues[TradeValueScaledByYAxisOption.Current] = {
    exportValue: sumExportValue,
    importValue: sumImportValue,
  };

  const deflator = deflatorForYearMatchingMetadata?.deflator;
  if (deflator) {
    tradeValues[TradeValueScaledByYAxisOption.Constant] = {
      exportValue: sumExportValue / deflator,
      importValue: sumImportValue / deflator,
    };
  }

  return tradeValues;
};

/**
 * Computes hierarchically transformed trade values
 */
const computeScaledTradeValuesHierarchical = ({
  partnersData,
  deflatorForYearMatchingMetadata,
  tradeFlow,
}: {
  partnersData: TradeDataItem[];
  deflatorForYearMatchingMetadata: DeflatorDatum | undefined;
  tradeFlow: TradeFlow;
}): HierarchicalTradeValues => {
  const tradeValues: HierarchicalTradeValues = {
    [TradeValueScaledByYAxisOption.Current]: { exportValue: undefined, importValue: undefined },
    [TradeValueScaledByYAxisOption.Constant]: { exportValue: undefined, importValue: undefined },
  };

  const sumExportValue = computeTotalSumByTradeFlow({
    data: partnersData,
    tradeFlow,
    tradeDirection: TradeDirection.Exports,
    scalingOption: TradeValueScaledByYAxisOption.Current,
  });
  const sumImportValue = computeTotalSumByTradeFlow({
    data: partnersData,
    tradeFlow,
    tradeDirection: TradeDirection.Imports,
    scalingOption: TradeValueScaledByYAxisOption.Current,
  });

  tradeValues[TradeValueScaledByYAxisOption.Current] = {
    exportValue: sumExportValue,
    importValue: sumImportValue,
  };

  const deflator = deflatorForYearMatchingMetadata?.deflator;
  if (deflator) {
    tradeValues[TradeValueScaledByYAxisOption.Constant] = {
      exportValue: sumExportValue !== undefined ? sumExportValue / deflator : undefined,
      importValue: sumImportValue !== undefined ? sumImportValue / deflator : undefined,
    };
  }

  return tradeValues;
};

/**
 * Process data by location detail level
 */
const processLocationDetailLevel = ({
  flatGroupedByPartnerAndYear,
  locationLevel,
  deflators,
  location,
  tradeFlow,
}: {
  flatGroupedByPartnerAndYear: EnrichedLocationItem[];
  locationLevel: LocationDetailLevel;
  deflators: DeflatorDatum[];
  location: string;
  tradeFlow: TradeFlow;
}) => {
  if (locationLevel === LocationDetailLevel.country) {
    return flatGroupedByPartnerAndYear;
  }

  const groupedByLocationDetailLevel: GroupedLocationItem[] = [];
  const deflatorsLookup = createYearLookupIndex(deflators);

  groups(flatGroupedByPartnerAndYear, (d: EnrichedLocationItem) => {
    if (locationLevel === LocationDetailLevel.region) {
      return d.hybrid_level_1.id;
    } else if (locationLevel === LocationDetailLevel.subregion) {
      return d.hybrid_level_2.id;
    }
  }).forEach(([partnerId, yearsDataForPartner]) => {
    const nameEn =
      locationLevel === LocationDetailLevel.region
        ? yearsDataForPartner[0].hybrid_level_1.nameEn
        : yearsDataForPartner[0].hybrid_level_2.nameEn;
    const topLevelParent = yearsDataForPartner[0].hybrid_level_1.id;

    groups(yearsDataForPartner, (d: EnrichedLocationItem) => d.year).forEach(([year, countriesForYear]) => {
      const deflatorForYear = deflatorsLookup.get(year);
      const tradeValues = computeScaledTradeValuesHierarchical({
        partnersData: countriesForYear as unknown as TradeDataItem[],
        deflatorForYearMatchingMetadata: deflatorForYear,
        tradeFlow,
      });

      groupedByLocationDetailLevel.push({
        countryId: location,
        partnerId,
        year,
        nameEn,
        topLevelParent,
        ...tradeValues,
      });
    });
  });

  // Sort by region
  groupedByLocationDetailLevel.sort((partnerA: GroupedLocationItem, partnerB: GroupedLocationItem) => {
    const groupNamesKeys = [...groupNames.keys()];
    return (
      groupNamesKeys.indexOf(partnerA.topLevelParent) -
      groupNamesKeys.indexOf(partnerB.topLevelParent)
    );
  });

  return groupedByLocationDetailLevel;
};

/**
 * Main transformation function for locations
 * Step 5: Uses createLocationHierarchyIndex + enrichLocationWithHierarchyFast for O(1) lookups
 * Step 6: Uses shared fillMissingYearObservations
 */
const transformLocations = ({
  untransformedData,
  location,
  locationDetailLevel,
  regions,
  subregions,
  countries,
  deflators,
  yearRangeForProductClass,
  tradeFlow,
}: {
  untransformedData: TradeDataItem[];
  location: string;
  locationDetailLevel: LocationDetailLevel;
  regions: RegionMetadata[];
  subregions: RegionMetadata[];
  countries: CountryMetadata[];
  deflators: DeflatorDatum[];
  yearRangeForProductClass: YearRangeForProductClass;
  tradeFlow: TradeFlow;
}): EnrichedLocationItem[] | GroupedLocationItem[] | undefined => {
  if (untransformedData.length === 0) {
    return undefined;
  }

  const locationLevel = getLocationLevelFromStringLocationId(location);

  // Step 6: Use shared fillMissingYearObservations
  const data = fillMissingYearObservations<TradeDataItem>({
    data: untransformedData,
    groupKey: 'partnerCountryId',
    yearRange: yearRangeForProductClass,
  });

  if (!location) {
    return undefined;
  }

  const deflatorsLookup = createYearLookupIndex(deflators);

  // Step 5: Pre-compute location hierarchy index for O(1) lookups
  const locationIndex = createLocationHierarchyIndex(
    regions,
    subregions,
    countries,
  );

  const flatGroupedByPartnerAndYear: EnrichedLocationItem[] = [];

  // Group data once
  const groupedData = groups(
    data,
    (d: TradeDataItem) => d.partnerCountryId,
    (d: TradeDataItem) => d.year
  );

  groupedData.forEach(([partnerCountryId, yearsData]) => {
    // Step 5: Use fast O(1) lookup instead of O(n) .find()
    const locationHierarchy = enrichLocationWithHierarchyFast(
      partnerCountryId,
      locationIndex,
    );

    yearsData.forEach(([year, partnersData]) => {
      const partnerLevel = partnersData[0].partnerLevel;
      const deflatorForYear = deflatorsLookup.get(year);

      const tradeValues = computeScaledTradeValues({
        partnersData,
        deflatorForYearMatchingMetadata: deflatorForYear,
        tradeFlow,
      });

      const locationIdKey =
        locationLevel === LocationLevel.Country ? 'countryId' : 'groupId';

      flatGroupedByPartnerAndYear.push({
        [locationIdKey]: location,
        locationLevel: 'country',
        partnerId: partnerCountryId,
        partnerLevel,
        year,
        partnersData,
        ...locationHierarchy,
        ...tradeValues,
      } as EnrichedLocationItem);
    });
  });

  return processLocationDetailLevel({
    flatGroupedByPartnerAndYear,
    locationLevel: locationDetailLevel,
    deflators,
    location,
    tradeFlow,
  });
};

export default transformLocations;
